import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import VideoPlayer from '../../../../common/VideoPlayer';
import { isSafeIframeUrl } from '../../../../common/utils/urlUtils';
import { ResourceDialogState, ResourceDialogNavigation } from './useResourceDialog';
import { PartnerProfileResourceType } from '../../../../common/models/PartnerProfile';
import {
  isResourcePreviewableInDialog,
  isInternalArticleLink,
  extractArticleId,
} from '../../../../common/utils/partnerResourceUtils';
import { ArticleContentDisplay } from '../../Content/ArticleContentDisplay';
import { Content } from '../../../../common/models/content/Content';
import { useAppDispatch } from '../../../../store/hooks';
import { getContentById } from '../../../../store/thunks/contentThunk';
import { ContentArticleViewSkeleton } from '../../Content/ContentArticleViewSkeleton';
import { useIsMobile } from '../../../../common/hooks/useResponsive';
import { useSolutionOrg } from '../../../../config/useSolutionOrg';

type ResourcePreviewDialogProps = {
  videoDialog: ResourceDialogState;
  pdfDialog: ResourceDialogState;
  onCloseVideo: () => void;
  onClosePdf: () => void;
  navigation?: ResourceDialogNavigation;
};

const NavigationButtons = ({ navigation }: { navigation: ResourceDialogNavigation }) => {
  const theme = useTheme();
  if (!navigation.show) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(0.5),
        p: theme.spacing(1.5),
        flexShrink: 0,
      }}
    >
      <IconButton
        onClick={navigation.prev}
        disabled={!navigation.canPrev}
        size="small"
        aria-label="previous resource"
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: theme.spacing(1),
          width: theme.spacing(4.5),
          height: theme.spacing(4.5),
          '&:disabled': {
            opacity: 0.4,
          },
        }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <IconButton
        onClick={navigation.next}
        disabled={!navigation.canNext}
        size="small"
        aria-label="next resource"
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: theme.spacing(1),
          width: theme.spacing(4.5),
          height: theme.spacing(4.5),
          '&:disabled': {
            opacity: 0.4,
          },
        }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export const ResourcePreviewDialog = ({
  videoDialog,
  pdfDialog,
  onCloseVideo,
  onClosePdf,
  navigation,
}: ResourcePreviewDialogProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const { isNativeAppExperience } = useSolutionOrg();
  const [articleContent, setArticleContent] = useState<Content | null>(null);
  const [articleLoading, setArticleLoading] = useState(false);

  // Check if the PDF dialog is actually showing an internal article
  const isShowingArticle = pdfDialog.open && isInternalArticleLink(pdfDialog.url);

  // Fetch article content when dialog opens with an internal article link
  useEffect(() => {
    if (isShowingArticle) {
      const contentId = extractArticleId(pdfDialog.url);
      if (contentId) {
        setArticleLoading(true);
        dispatch(getContentById(contentId))
          .then(unwrapResult)
          .then((content) => {
            setArticleContent(content);
          })
          .catch(() => {
            setArticleContent(null);
          })
          .finally(() => {
            setArticleLoading(false);
          });
      }
    } else {
      setArticleContent(null);
    }
  }, [isShowingArticle, pdfDialog.url, dispatch]);

  // For DOC files and PDFs on mobile or native app, use Google Docs Viewer
  // For internal case study links, prepend the current origin
  // PDFs use native browser rendering on desktop
  const getIframeUrl = (url: string, resourceType?: PartnerProfileResourceType) => {
    const googleViewerUrl = (pdfUrl: string) => `https://docs.google.com/gview?url=${encodeURIComponent(pdfUrl)}&embedded=true`;

    if (resourceType === PartnerProfileResourceType.DOC) {
      return googleViewerUrl(url);
    }
    // Use Google Docs Viewer for PDFs on mobile or native app webview
    if (resourceType === PartnerProfileResourceType.PDF && (isMobile || isNativeAppExperience)) {
      return googleViewerUrl(url);
    }
    // Handle internal links
    if (resourceType === PartnerProfileResourceType.CASE_STUDY && url.startsWith('/')) {
      return `${window.location.origin}${url}`;
    }
    return url;
  };

  return (
    <>
      {/* Video Dialog */}
      <Dialog
        open={videoDialog.open}
        onClose={onCloseVideo}
        maxWidth="md"
        fullWidth
        aria-labelledby="resource-video-dialog-title"
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: theme.spacing(2),
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          id="resource-video-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: theme.spacing(2),
          }}
        >
          <Typography variant="h15" component="span">
            {videoDialog.title || t('partnerDetails.resources')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onCloseVideo}
            sx={{
              color: theme.palette.grey[600],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {videoDialog.url && (
            <VideoPlayer
              videoUrl={videoDialog.url}
              playing={false}
              optionalVideoContainerStyle={{
                width: '100%',
                aspectRatio: { xs: '4 / 3', sm: '16 / 9' },
                background: theme.palette.common.black,
              }}
            />
          )}
        </DialogContent>
        {navigation && <NavigationButtons navigation={navigation} />}
      </Dialog>

      {/* PDF/Document Dialog */}
      <Dialog
        open={pdfDialog.open}
        onClose={onClosePdf}
        maxWidth="md"
        fullWidth
        aria-labelledby="resource-pdf-dialog-title"
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: theme.spacing(2),
            overflow: 'hidden',
            height: '85vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          id="resource-pdf-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: theme.spacing(2),
          }}
        >
          <Typography variant="h15" component="span">
            {pdfDialog.title || t('partnerDetails.resources')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClosePdf}
            sx={{
              color: theme.palette.grey[600],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{
          p: isShowingArticle ? theme.spacing(3) : 0,
          overflow: isShowingArticle ? 'auto' : 'hidden',
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
        }}>
          {(() => {
            // If it's an internal article link, show article content
            if (isShowingArticle) {
              const contentElement = (() => {
                if (articleLoading) {
                  return <ContentArticleViewSkeleton showActions={false} />;
                }
                if (articleContent) {
                  return <ArticleContentDisplay content={articleContent} isPreview />;
                }
                return <Typography>Failed to load article</Typography>;
              })();

              return (
                <Box sx={{ maxWidth: theme.spacing(100), width: '100%' }}>
                  {contentElement}
                </Box>
              );
            }

            // For PDF files: use Google Docs Viewer on mobile, native browser on desktop
            if (pdfDialog.url && pdfDialog.resourceType === PartnerProfileResourceType.PDF) {
              if (!isSafeIframeUrl(pdfDialog.url)) {
                return <Typography>{t('partnerDetails.unableToLoadDocument')}</Typography>;
              }
              return (
                <iframe
                  src={getIframeUrl(pdfDialog.url, pdfDialog.resourceType)}
                  title={pdfDialog.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              );
            }

            // For DOC and internal CASE_STUDY files, use iframe
            if (pdfDialog.url &&
              pdfDialog.resourceType &&
              isResourcePreviewableInDialog(pdfDialog.resourceType, pdfDialog.url)) {
              // Only check safe URL for DOC files (external URLs)
              if (pdfDialog.resourceType === PartnerProfileResourceType.DOC &&
                  !isSafeIframeUrl(pdfDialog.url)) {
                return <Typography>{t('partnerDetails.unableToLoadDocument')}</Typography>;
              }

              return (
                <iframe
                  src={getIframeUrl(pdfDialog.url, pdfDialog.resourceType)}
                  title={pdfDialog.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              );
            }

            return null;
          })()}
        </DialogContent>
        {navigation && <NavigationButtons navigation={navigation} />}
      </Dialog>
    </>
  );
};
